import { supabase } from '../lib/supabase';
import { subscriptionService } from './subscriptionService';

/**
 * Candidate Unlock Service
 * Manages candidate unlock mechanics for freemium and paid plans
 */

/**
 * Check if company can unlock a candidate
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} - Can unlock result
 */
export const canUnlockCandidate = async (companyId) => {
  try {
    // Get subscription and usage metrics
    const metricsResult = await subscriptionService?.getUsageMetrics(companyId);
    if (!metricsResult?.success) throw new Error('Failed to fetch metrics');

    const { plan, candidateUnlocks } = metricsResult?.data;

    // Free plan (admin-assigned) and paid plans have unlimited unlocks
    if (plan === 'free' || plan === 'standard' || plan === 'premium') {
      return {
        success: true,
        canUnlock: true,
        remaining: -1, // Unlimited
        plan
      };
    }

    // Freemium plan - check monthly quota
    const hasUnlocksRemaining = candidateUnlocks?.remaining > 0;

    return {
      success: true,
      canUnlock: hasUnlocksRemaining,
      remaining: candidateUnlocks?.remaining,
      used: candidateUnlocks?.used,
      limit: candidateUnlocks?.limit,
      resetAt: candidateUnlocks?.resetAt,
      plan
    };
  } catch (error) {
    console.error('Error checking unlock availability:', error);
    return {
      success: false,
      canUnlock: false,
      error: error?.message
    };
  }
};

/**
 * Get remaining unlocks for current month
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} - Remaining unlocks
 */
export const getRemainingUnlocks = async (companyId) => {
  try {
    const result = await canUnlockCandidate(companyId);
    return result;
  } catch (error) {
    console.error('Error getting remaining unlocks:', error);
    return {
      success: false,
      error: error?.message
    };
  }
};

/**
 * Unlock a candidate
 * @param {string} companyId - Company UUID
 * @param {string} applicationId - Application UUID
 * @param {string} userId - User UUID who is unlocking
 * @returns {Promise<Object>} - Unlock result
 */
export const unlockCandidate = async (companyId, applicationId, userId) => {
  try {
    // Check if already unlocked
    const { data: existingUnlock, error: checkError } = await supabase?.from('candidate_unlocks')?.select('*')?.eq('company_id', companyId)?.eq('application_id', applicationId)?.single();

    if (existingUnlock) {
      return {
        success: true,
        alreadyUnlocked: true,
        data: existingUnlock
      };
    }

    // Check if can unlock
    const canUnlockResult = await canUnlockCandidate(companyId);
    if (!canUnlockResult?.success || !canUnlockResult?.canUnlock) {
      return {
        success: false,
        error: 'No unlocks remaining this month. Upgrade to Standard plan for unlimited access.',
        canUnlock: false,
        remaining: canUnlockResult?.remaining
      };
    }

    const plan = canUnlockResult?.plan;
    const unlockType = plan === 'freemium' ? 'freemium_monthly' : 'paid_unlimited';

    // Create unlock record
    const { data: unlockData, error: unlockError } = await supabase?.from('candidate_unlocks')?.insert({
        company_id: companyId,
        application_id: applicationId,
        unlocked_by: userId,
        unlock_type: unlockType
      })?.select()?.single();

    if (unlockError) throw unlockError;

    // Update monthly quota for freemium
    if (plan === 'freemium') {
      const currentMonth = new Date()?.toISOString()?.slice(0, 7); // YYYY-MM

      // First get current value
      const { data: currentQuota } = await supabase?.from('monthly_unlock_quotas')?.select('unlocks_used')?.eq('company_id', companyId)?.eq('month_year', currentMonth)?.single();

      const newUnlocksUsed = (currentQuota?.unlocks_used || 0) + 1;

      const { error: quotaError } = await supabase?.from('monthly_unlock_quotas')?.update({ unlocks_used: newUnlocksUsed })?.eq('company_id', companyId)?.eq('month_year', currentMonth);

      if (quotaError) {
        console.error('Error updating quota:', quotaError);
        // Don't fail the unlock if quota update fails
      }
    }

    return {
      success: true,
      data: unlockData,
      remaining: plan === 'freemium' ? (canUnlockResult?.remaining - 1) : -1
    };
  } catch (error) {
    console.error('Error unlocking candidate:', error);
    return {
      success: false,
      error: error?.message
    };
  }
};

/**
 * Get all unlocked candidates for a company
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} - Unlocked candidates
 */
export const getUnlockedCandidates = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('candidate_unlocks')?.select(`
        *,
        job_applications (
          id,
          applicant_id,
          job_id,
          status,
          created_at
        )
      `)?.eq('company_id', companyId)?.order('unlocked_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Error fetching unlocked candidates:', error);
    return {
      success: false,
      error: error?.message,
      data: []
    };
  }
};

/**
 * Check if specific application is unlocked
 * @param {string} companyId - Company UUID
 * @param {string} applicationId - Application UUID
 * @returns {Promise<Object>} - Is unlocked result
 */
export const isApplicationUnlocked = async (companyId, applicationId) => {
  try {
    const { data, error } = await supabase?.from('candidate_unlocks')?.select('*')?.eq('company_id', companyId)?.eq('application_id', applicationId)?.single();

    return {
      success: true,
      isUnlocked: !!data
    };
  } catch (error) {
    return {
      success: true,
      isUnlocked: false
    };
  }
};

export default {
  canUnlockCandidate,
  getRemainingUnlocks,
  unlockCandidate,
  getUnlockedCandidates,
  isApplicationUnlocked
};